import { useState } from 'react';
import { useTranslate } from 'react-admin';
import { Alert, Stack, Typography } from '@mui/material';

import {
  approveQuarantineRefund,
  cancelQuarantineRefund,
  holdQuarantinedTransfer,
  proposeQuarantineRefund,
  releaseQuarantineHold,
  type QuarantineRefund,
  type TransferItem,
  type TransferList,
} from 'src/pages/admin/services/vaultOperationsService';

import { ConfirmDialog, type ConfirmSpec } from './ConfirmDialog';
import { OPERATIONS_NS as NS } from './constants';
import {
  adminName,
  durationText,
  minutesUntil,
  money2,
  money6,
  refundLegs,
  sumLine,
  requiredText,
} from './helpers';
import {
  ActionButton,
  AddressLink,
  EmptyState,
  ItemCard,
  ListNotices,
  nodes,
  ProgressStrip,
  TabIntro,
  Term,
  TimeText,
  useNow,
} from './parts';
import type { Queue } from './useQueue';

interface Props {
  queue: Queue<TransferList>;
  vaultName: (vaultId: string | null | undefined) => string;
  onSettled: () => Promise<unknown>;
}

/** "Transfers to refund": money paid in from an unknown sender. It is refunded, never attributed by hand. */
export const TransfersTab = ({ queue, vaultName, onSettled }: Props) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.transfers.${key}`, options);
  const c = (key: string, options?: object) => translate(`${NS}.confirm.${key}`, options);
  const [spec, setSpec] = useState<ConfirmSpec | null>(null);
  const now = useNow();

  // The page reads the list for the vault chosen on its switcher, so the rows, the count and the notices agree.
  const items = queue.data?.items ?? [];

  /** Both legs of a refund (rule 2: amount, from, to), from the BFF's figures. */
  const legsText = (item: TransferItem, onCard = false) => {
    const legs = refundLegs(item.refund, onCard ? money2 : money6);
    if (!legs) return t('legs.unavailable');
    // An account by name, with its address (short, copy, explorer) when the BFF serves it.
    const account = (name: string, address: string | null, term?: 'cold' | 'payout') => (
      <>
        {term ? <Term term={term}>{name}</Term> : name}
        {address && (
          <>
            {' ('}
            <AddressLink address={address} />
            {')'}
          </>
        )}
      </>
    );
    const sender = legs.toSender.toAddress ?? item.refundProposal?.sender ?? null;
    return (
      <Stack spacing={0.5} component="span">
        <span data-testid="leg-cold">
          {nodes((v) => t('legs.toCold', { amount: legs.vaultToCold.amount, ...v }), {
            from: account(t('legs.vaultAccount'), legs.vaultToCold.fromAddress),
            to: account(t('legs.coldAccount'), legs.vaultToCold.toAddress, 'cold'),
          })}
        </span>
        <span data-testid="leg-sender">
          {nodes((v) => t('legs.toSender', { amount: legs.toSender.amount, ...v }), {
            from: account(t('legs.payoutAccount'), legs.toSender.fromAddress, 'payout'),
            to: sender ? (
              <>
                (<AddressLink address={sender} />)
              </>
            ) : (
              '—'
            ),
          })}
        </span>
        {item.refund?.toSender.amount != null && (
          <span data-testid="leg-equation">
            {(([received, fee, back]) => t('legs.equation', { received, fee, back }))(
              onCard
                ? sumLine(
                    item.refund.vaultToCold.amount,
                    item.refund.fee,
                    item.refund.toSender.amount
                  )
                : [legs.vaultToCold.amount, legs.fee, legs.toSender.amount]
            )}
          </span>
        )}
      </Stack>
    );
  };

  const withdraw = (item: TransferItem, refund: QuarantineRefund) =>
    setSpec({
      title: t('withdrawTitle'),
      summary: <ProgressStrip proposal={refund} youProposed={refund.youProposed} />,
      moneyMoves: false,
      money: t('withdrawNoMoney'),
      approver: c('noApproval'),
      ifNobody: t('withdrawIfNobody'),
      confirmLabel: t('withdraw'),
      fields: [],
      submit: () => cancelQuarantineRefund(item.vaultId, refund.id),
      doneMessage: t('done.cancel'),
    });

  const approve = (item: TransferItem, refund: QuarantineRefund) =>
    setSpec({
      title: t('approveTitle', { amount: money2(refund.payoutAmount) }),
      summary: (
        <Stack spacing={0.5}>
          <ProgressStrip proposal={refund} youProposed={false} />
          <Typography variant="body2">
            {c('proposerNote', { name: adminName(refund.proposedByAdmin, refund.proposedBy) })} “
            {refund.note}”
          </Typography>
          <Typography variant="body2">
            {c('evidence')}: {refund.evidenceRef}
          </Typography>
        </Stack>
      ),
      moneyMoves: true,
      money: legsText(item),
      approver: c('approverFinal'),
      ifNobody: c('ifNobodyApprove'),
      stepUpReason: t('stepUp.refund'),
      confirmLabel: t('approve', { amount: money2(refund.payoutAmount) }),
      fields: [],
      submit: () => approveQuarantineRefund(item.vaultId, refund.id),
      doneMessage: t('done.approve'),
    });

  const propose = (item: TransferItem, now_: boolean) =>
    setSpec({
      title: now_
        ? t('refundNowTitle')
        : t('proposeTitle', { amount: money2(item.refund?.toSender.amount) }),
      moneyMoves: false,
      money: (
        <>
          {t('ifApproved')} {legsText(item)}
        </>
      ),
      approver: c('approverNext'),
      ifNobody: now_ ? t('refundNowIfNobody') : c('ifNobodyPropose'),
      confirmLabel: now_
        ? t('refundNow', { amount: money2(item.refund?.toSender.amount) })
        : t('propose', { amount: money2(item.refund?.toSender.amount) }),
      blocked: item.refundPreviewUnavailable ? t('previewUnavailable') : undefined,
      fields: [
        {
          name: 'evidenceRef',
          label: c('evidenceRef'),
          hint: t('evidenceHint'),
          validate: requiredText,
        },
        {
          name: 'note',
          label: c('note'),
          multiline: true,
          optional: true,
          validate: (v) => (v.trim() ? requiredText(v) : undefined),
        },
      ],
      submit: (v, requestId) =>
        proposeQuarantineRefund(item.vaultId, item.transferId, {
          evidenceRef: v.evidenceRef,
          ...(v.note ? { note: v.note } : {}),
          requestId,
        }),
      doneMessage: t('done.propose'),
    });

  /** With an automatic refund pending, Hold stops it; with none, there is nothing to stop, it blocks any refund. */
  const holdLabel = (item: TransferItem) => t(item.autoResolvesAt ? 'hold' : 'holdBlock');

  const hold = (item: TransferItem) =>
    setSpec({
      title: t('holdTitle'),
      moneyMoves: false,
      money: t('holdNoMoney'),
      approver: t('holdApprover'),
      ifNobody: t('holdIfNobody'),
      confirmLabel: holdLabel(item),
      fields: [
        {
          name: 'reason',
          label: t('holdReason'),
          hint: t('holdReasonHint'),
          multiline: true,
          validate: requiredText,
        },
      ],
      submit: (v) => holdQuarantinedTransfer(item.vaultId, item.transferId, v.reason),
      doneMessage: t('done.hold'),
    });

  const release = (item: TransferItem) =>
    setSpec({
      title: t('releaseTitle'),
      summary: item.hold && (
        <Typography variant="body2">{t('heldFor', { reason: item.hold.reason ?? '—' })}</Typography>
      ),
      // The BFF says releasing a hold moves no money itself (nextStep.moneyMoves); the sentence says what may follow.
      moneyMoves: item.nextStep.moneyMoves,
      money: (
        <>
          {t('releaseMoney')} {legsText(item)}
        </>
      ),
      approver: t('releaseApprover'),
      ifNobody: t('releaseIfNobody'),
      stepUpReason: t('stepUp.release'),
      confirmLabel: t('release'),
      fields: [],
      submit: () => releaseQuarantineHold(item.vaultId, item.transferId),
      doneMessage: t('done.release'),
    });

  const failed = queue.data?.failedVaults ?? [];
  return (
    <Stack spacing={2}>
      <TabIntro tab="transfers" />
      {queue.data && (
        <ListNotices
          consistent={queue.data.consistent}
          truncated={queue.data.truncated}
          shown={queue.data.items.length}
          onRefresh={queue.refetch}
        />
      )}
      {failed.length > 0 && (
        <Alert severity="warning" data-testid="failed-vaults">
          {t('failedVaults', { vaults: failed.map((id) => vaultName(id)).join(', ') })}
        </Alert>
      )}
      {queue.data && items.length === 0 && (
        <EmptyState tab="transfers" checkedAt={queue.data.checkedAt} />
      )}
      {items.map((item) => {
        const refund = item.refundProposal;
        const kind = item.nextStep.kind;
        const left = minutesUntil(item.autoResolvesAt, now);
        const eta = left !== null && left >= 0 ? durationText(left, translate) : null;
        const lastHold = item.hold?.lastConsoleHold;
        const refundLabel = money2(item.refund?.toSender.amount);
        return (
          <ItemCard
            key={`${item.vaultId}/${item.transferId}`}
            testId={`transfer-${item.transferId}`}
            title={t('title', { amount: money2(item.amount), vault: vaultName(item.vaultId) })}
            status={item.uxStatus}
            statusDetail={
              item.uxStatus === 'WAITING_AUTOMATIC' && eta
                ? t('autoIn', { time: eta })
                : item.uxStatus === 'HELD' && item.hold?.reason
                  ? item.hold.reason
                  : null
            }
            whatHappened={translate(
              `${NS}.whatHappened.transfers.${
                kind === 'REFUND_NOT_POSSIBLE'
                  ? 'BLOCKED'
                  : item.uxStatus === 'HELD' ||
                      item.uxStatus === 'WAITING_SECOND_APPROVAL' ||
                      item.uxStatus === 'WAITING_AUTOMATIC'
                    ? item.uxStatus
                    : 'NEEDS_ACTION'
              }`,
              { amount: money2(item.amount) }
            )}
            affected={
              <Stack spacing={0.5} component="span">
                <span>
                  {t('affected', { vault: vaultName(item.vaultId), amount: money2(item.amount) })}
                </span>
                <span>
                  {t('arrived')} <TimeText iso={item.arrivedAt} />
                </span>
                {item.refund && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    component="span"
                    sx={{ alignItems: 'center' }}
                  >
                    <span>{t('sender')}</span>
                    <AddressLink address={item.refund.toSender.toAddress} />
                  </Stack>
                )}
              </Stack>
            }
            extra={
              <>
                {refund && <ProgressStrip proposal={refund} youProposed={refund.youProposed} />}
                {item.hold && (
                  <Typography variant="body2" data-testid="hold-info">
                    {t('holdInfo', { reason: item.hold.reason ?? '—' })}{' '}
                    {lastHold
                      ? t('lastConsoleHold', {
                          name: adminName(lastHold.heldByAdmin, lastHold.heldBy),
                        })
                      : t('holderUnknown')}
                  </Typography>
                )}
                {item.refund && !item.hold && (
                  <Stack spacing={0.5} data-testid="refund-legs">
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('ifRefunded')}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {legsText(item, true)}
                    </Typography>
                  </Stack>
                )}
              </>
            }
            deadline={
              item.autoResolvesAt ? (
                <>
                  {t('autoRefundAt')} <TimeText iso={item.autoResolvesAt} />
                  {eta && ` (${t('autoIn', { time: eta })})`}
                </>
              ) : (
                t(item.hold ? 'heldDeadline' : 'noAutoRefund')
              )
            }
            actions={
              <>
                {kind === 'PROPOSE_REFUND' && (
                  <ActionButton
                    needs="propose"
                    variant="contained"
                    disabled={item.refundPreviewUnavailable}
                    onClick={() => propose(item, false)}
                  >
                    {t('propose', { amount: refundLabel })}
                  </ActionButton>
                )}
                {kind === 'WAIT_OR_REFUND_NOW' && (
                  <ActionButton
                    needs="propose"
                    variant="contained"
                    disabled={item.refundPreviewUnavailable}
                    onClick={() => propose(item, true)}
                  >
                    {t('refundNow', { amount: refundLabel })}
                  </ActionButton>
                )}
                {item.refundPreviewUnavailable && kind !== 'RELEASE_HOLD' && (
                  <Typography variant="body2" sx={{ alignSelf: 'center', color: 'warning.main' }}>
                    {t('previewUnavailable')}
                  </Typography>
                )}
                {kind === 'WAIT_FOR_SECOND_APPROVER' && refund && (
                  <>
                    <ActionButton
                      needs="propose"
                      variant="outlined"
                      onClick={() => withdraw(item, refund)}
                    >
                      {t('withdraw')}
                    </ActionButton>
                  </>
                )}
                {kind === 'APPROVE_REFUND' && refund && (
                  <>
                    <ActionButton
                      needs="approve"
                      variant="contained"
                      onClick={() => approve(item, refund)}
                    >
                      {t('approve', { amount: money2(refund.payoutAmount) })}
                    </ActionButton>
                    <ActionButton
                      needs="propose"
                      variant="outlined"
                      onClick={() => withdraw(item, refund)}
                    >
                      {t('reject')}
                    </ActionButton>
                  </>
                )}
                {kind === 'RELEASE_HOLD' && (
                  <ActionButton needs="approve" variant="contained" onClick={() => release(item)}>
                    {t('release')}
                  </ActionButton>
                )}
                {kind === 'REFUND_NOT_POSSIBLE' && (
                  <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                    {t('notPossible')}
                  </Typography>
                )}
                {(kind === 'PROPOSE_REFUND' || kind === 'WAIT_OR_REFUND_NOW') && (
                  <ActionButton
                    needs="propose"
                    variant="outlined"
                    color="secondary"
                    onClick={() => hold(item)}
                  >
                    {holdLabel(item)}
                  </ActionButton>
                )}
              </>
            }
            technical={{
              transferId: item.transferId,
              vaultId: item.vaultId,
              status: item.status,
              ...item.technical,
              proposalId: refund?.id,
              refundPath: refund?.path,
              ageSeconds: item.ageSeconds,
            }}
          />
        );
      })}
      {spec && <ConfirmDialog spec={spec} onClose={() => setSpec(null)} onSettled={onSettled} />}
    </Stack>
  );
};
